import React, {Fragment, useState, useRef, useEffect, useLayoutEffect, useContext} from "react";
import { AppContext } from "../../components/AppProvider";
import styled from "styled-components";
import LoadingShimmer from "../../components/UI/LoadingShimmer";

// UI
import TypeALayout from "./TypeA/Layout/Layout";
import TypeBLayout from "./TypeB/Layout/Layout";
import TypeELayout from "./TypeE/Layout/Layout";

import { _API, _CK, _U } from "../../modules/utils";
import Socket from "../../components/UI/Socket";
import useStore from "../../modules/utils/store";

const ConsoleViews = (props) => {
  const { history, location, match } = props;
  const { eventIdx : eventIdx, sessionIdx: sessionIdx, type: type } = match.params;
  const { siteInfo }= useStore();
  const resolvedSiteIdx = siteInfo?.siteIdx || siteInfo?.idx;
  const [loaded, setLoaded] = useState(false);
  const { iam, iamChecked } = useContext(AppContext);
  const [consoleCfg, setConsoleCfg] = useState({});
  const [sessionAttendance, setSessionAttendance] = useState();
  const hasInitializedConsoleCount = useRef(false);
  const hasRequestedConsoleInfo = useRef(false);
  const hasHandledMissingIam = useRef(false);
  const isMountedRef = useRef(true);
  
  //소켓
  const [isConnect, setIsConnect] = useState(false);
  const debugSocket = false; //_CFG.isLocal();
  const hasSocketIdentity = (value) =>
    value !== undefined && value !== null && value !== "";
  const isSocketReady =
    hasSocketIdentity(siteInfo?.siteIdx) &&
    hasSocketIdentity(consoleCfg?.eventIdx) &&
    hasSocketIdentity(consoleCfg?.sessionIdx) &&
    hasSocketIdentity(consoleCfg?.consoleIdx);

  const onConnected = () => {

    setIsConnect(true);
    _U.postMessage("SOCKET-SEND", {
        action: 'CONNECT',
        data: {
            mode: 'CONNECT',
            siteIdx: siteInfo?.siteIdx,
            eventIdx: eventIdx || consoleCfg?.eventIdx,
            sessionIdx: consoleCfg?.sessionIdx,
            consoleIdx: consoleCfg?.consoleIdx,
            name: iam?.name,
            memType: iam?.memType,
            companyName: iam?.companyName,
            deptName: iam?.deptName,
            passCode: iam?.passCode,
            preregIdx: iam?.preregIdx,
            sessionName: consoleCfg?.sessionName,
            logKey:iam?.logKey || _CK.getLogKey(),
            consoleCountView:iam?.consoleCountView
        },
    });
  };

  const onDisconnected = () => {
    setIsConnect(false);
    // 일시적 소켓 끊김(자동 재연결)에서는 DISCONNECT를 보내지 않는다.
    // DISCONNECT는 "세션 이탈"(세션 SET 제거 + CONSOLE_OUT 기록)을 의미하므로,
    // 의도적 이탈(로그아웃/페이지 이동: isDisconnecting 플래그)에서만 전송한다.
    // (자동 재연결 중 DISCONNECT를 보내 스스로 세션에서 빠지고 재연결→DISCONNECT를
    //  반복하던 무한루프로 실시간 수신이 끊기던 문제 수정. 죽은 연결은 서버 TTL 180초로 정리)
    if (sessionStorage.getItem('isDisconnecting') !== 'true') {
      return;
    }
    _U.postMessage("SOCKET-SEND", {
        action: 'DISCONNECT',
        data: {
            mode:'DISCONNECT',
            siteIdx: siteInfo?.siteIdx,
            eventIdx: siteInfo?.eventIdx,
            sessionIdx: consoleCfg?.sessionIdx,
            consoleIdx: consoleCfg?.consoleIdx,
            name: iam?.name,
            memType: iam?.memType,
            companyName: iam?.companyName,
            deptName: iam?.deptName,
            passCode:iam?.passCode,
            preregIdx: iam?.preregIdx,
            sessionName: consoleCfg?.sessionName,
            logKey:iam?.logKey || _CK.getLogKey(),
            consoleCountView:iam?.consoleCountView
        },
    });

  };


  /**
   * 화면 초기화
   *
   * initPage()
   *  - 버튼 클릭 이벤트
   */
  useLayoutEffect(() => {
    if (hasInitializedConsoleCount.current) {
      return;
    }

    hasInitializedConsoleCount.current = true;

    // 🔧 새로고침 감지: sessionStorage 사용
    const isRefresh = sessionStorage.getItem('isRefreshing');
    
    if (isRefresh === 'true') {
      // 새로고침 → consoleCountView 증가시키지 않음
      sessionStorage.removeItem('isRefreshing');
    } else {
      // 최초 접속 또는 재입장 → consoleCountView 증가
      _CK.setConsoleCountView(parseInt(_CK.getConsoleCountView()) + 1);
    }
  }, []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (hasRequestedConsoleInfo.current) {
      return;
    }

    if (!resolvedSiteIdx || !sessionIdx) {
      return;
    }

    hasRequestedConsoleInfo.current = true;
    getConsoleInfo();
  }, [resolvedSiteIdx, sessionIdx]);

  useEffect(() => {
    if (!iamChecked || iam || hasHandledMissingIam.current) {
      return;
    }

    hasHandledMissingIam.current = true;

    if (history?.replace && resolvedSiteIdx) {
      history.replace(`/site/${resolvedSiteIdx}/event/list`);
    } else {
      window.location.href = `/`;
    }
  }, [history, iam, iamChecked, resolvedSiteIdx]);

  // ✅ iam 로드 후 출석 정보 조회 (directLogin 타이밍 이슈 해결)
  useEffect(() => {
    if (iam?.passCode || iam?.preregIdx) {
      getSessionAttendanceInfo();
    }
  }, [iam]);

  // ✅ iam.preregIdx가 소켓 연결 이후 로드된 경우 CONNECT 재전송 (ECS 참가자 카운트 0명 버그 수정)
  const prevPreregIdxRef = useRef(null);
  useEffect(() => {
    if (!iam?.preregIdx) return;
    if (prevPreregIdxRef.current === iam.preregIdx) return; // 이미 전송한 preregIdx면 스킵
    if (!isConnect) return; // 소켓 미연결 상태면 스킵 (onConnected에서 보낼 예정)
    prevPreregIdxRef.current = iam.preregIdx;
    _U.postMessage("SOCKET-SEND", {
      action: 'CONNECT',
      data: {
        mode: 'CONNECT',
        siteIdx: siteInfo?.siteIdx,
        eventIdx: eventIdx || consoleCfg?.eventIdx,
        sessionIdx: consoleCfg?.sessionIdx,
        consoleIdx: consoleCfg?.consoleIdx,
        name: iam?.name,
        memType: iam?.memType,
        companyName: iam?.companyName,
        deptName: iam?.deptName,
        passCode: iam?.passCode,
        preregIdx: iam?.preregIdx,
        sessionName: consoleCfg?.sessionName,
        logKey: _CK.getLogKey(),
        consoleCountView: _CK.getConsoleCountView(),
      },
    });
  }, [iam?.preregIdx, isConnect]);

  // STREAM_ONLINE/OFFLINE 수신 시 consoleCfg 갱신 (NCloud 채널 토글 연동)
  useEffect(() => {
    const onMessage = (e) => {
      _U.parseMessage(e).then((data) => {
        if (data.code !== "SOCKET-RECEIVED") return;
        const d = data.data ? JSON.parse(data.data) : null;
        if (d?.mode === "STREAM_ONLINE" || d?.mode === "STREAM_OFFLINE" ||
            d?.mode === "TEST_STREAM_ONLINE" || d?.mode === "TEST_STREAM_OFFLINE") {
          getConsoleInfo(true);
        }
      });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // 이벤트 사이트 환경설정 조회용 (템플릿, 테마 등 설정 필요)
  // isRefresh=true: STREAM 이벤트로 인한 백그라운드 갱신 - 실패 시 alert/redirect 없이 무시
  const getConsoleInfo = async (isRefresh = false) => {
    if (!resolvedSiteIdx || !sessionIdx) {
      return;
    }

    const req = {
      path: "/v1/client/console/info",
      isNoErrorNotify: true,
      data: {
        eventIdx: eventIdx,
        sessionIdx: sessionIdx,
        siteIdx: resolvedSiteIdx
      },
    };

    await _API.post(req).then((res) => {
      const { data } = res;

      if (!isMountedRef.current) {
        return;
      }

      if (data) {
        setConsoleCfg(prev => {
          // consoleEndPoint가 같으면 동일 참조 반환 → consoleCfg 변경 없음 → Player re-render 방지
          // 2창(TypeA+TypeB) 동시 사용 시 이중 JWPlayer 재초기화 방지
          // 단, consoleEndPoint가 null인 경우(non-NCloud 서버)는 urlKeyUrl도 비교 → STREAM_OFFLINE 시 urlKeyUrl 변경 반영
          if (prev?.consoleEndPoint === data?.consoleEndPoint && prev?.urlKeyUrl === data?.urlKeyUrl) return prev;
          return data;
        });
        setLoaded(true);
        return;
      }
      throw new Error("콘솔 정보를 불러오지 못했습니다.");
    }).catch((err) => {
      console.error("[ConsoleViews] getConsoleInfo failed", err);

      // STREAM 이벤트 갱신 실패는 무시 (기존 consoleCfg 유지)
      if (isRefresh) return;

      hasRequestedConsoleInfo.current = false;
      alert("콘솔 정보를 불러오지 못했습니다.");

      if (history?.replace && resolvedSiteIdx) {
        history.replace(`/site/${resolvedSiteIdx}/event/list`);
      } else {
        window.location.href = `/`;
      }
    });
  };


  const getSessionAttendanceInfo = async () => {
    // ✅ React Router 사용으로 Context 유지, iam 바로 사용 가능
    // (단, 직접 URL 접근 시를 대비한 fallback은 유지)
    let passCodeToUse = iam?.passCode;
    
    if (!passCodeToUse) {
      const authToken = _CK.getAuth();
      if (authToken) {
        try {
          const decoded = JSON.parse(atob(authToken.split('.')[1]));
          passCodeToUse = decoded?.passCode || '';
        } catch (e) {
          console.error('[getSessionAttendanceInfo] Failed to decode JWT:', e);
        }
      }
    }
    
    const req = {
      path: "/v1/client/session/attendance/info",
      data: {
        sessionIdx: sessionIdx,
        passCode: passCodeToUse
      },
    };

    await _API.post(req).then((res) => {
      let { data } = res;
      if (!isMountedRef.current) {
        return;
      }
      if (data) {
        setSessionAttendance(data);
      }
    }).catch((err) => {
      console.error('[getSessionAttendanceInfo] Error:', err);
    });
  };

  if (!iamChecked || !loaded)
    return (
      <> 
                <LoadingShimmer />
      </>
    );
  else
    if(consoleCfg.consoleTemplateIdx == 6){
      return (
        <Fragment>
          <TypeALayout
            history={history}
            consoleCfg={consoleCfg}
            sessionAttendance={sessionAttendance}
            getSessionAttendanceInfo={getSessionAttendanceInfo}
          >
          </TypeALayout>
          {isSocketReady && (
            <SocketWrapper debug={debugSocket}>
              <Socket
                siteInfo={siteInfo}
                consoleCfg={consoleCfg}
                debug={debugSocket}
                sessionIdx={sessionIdx}
                eventIdx={eventIdx}
                onConnected={onConnected}
                onDisconnected={onDisconnected}
              />
            </SocketWrapper>
          )}
        </Fragment>
      );
    }else if(consoleCfg.consoleTemplateIdx == 7){
      return (
        <Fragment>
          <TypeBLayout
            history={history}
            consoleCfg={consoleCfg}
            sessionAttendance={sessionAttendance}
            getSessionAttendanceInfo={getSessionAttendanceInfo}
          >
          </TypeBLayout>
          {isSocketReady && (
            <SocketWrapper debug={debugSocket}>
              <Socket
                siteInfo={siteInfo}
                consoleCfg={consoleCfg}
                debug={debugSocket}
                sessionIdx={sessionIdx}
                eventIdx={eventIdx}
                onConnected={onConnected}
                onDisconnected={onDisconnected}
              />
            </SocketWrapper>
          )}
        </Fragment>
      );
    }else if(consoleCfg.consoleTemplateIdx == 14){
      return (
        <Fragment>
          <TypeELayout
            history={history}
            consoleCfg={consoleCfg}
            sessionAttendance={sessionAttendance}
            getSessionAttendanceInfo={getSessionAttendanceInfo}
          >
          </TypeELayout>
          {isSocketReady && (
            <SocketWrapper debug={debugSocket}>
              <Socket
                siteInfo={siteInfo}
                consoleCfg={consoleCfg}
                debug={debugSocket}
                sessionIdx={sessionIdx}
                eventIdx={eventIdx}
                onConnected={onConnected}
                onDisconnected={onDisconnected}
              />
            </SocketWrapper>
          )}
        </Fragment>
      );
    }else{
      return (
        <Fragment>
          <TypeALayout
            history={history}
            consoleCfg={consoleCfg}
            sessionAttendance={sessionAttendance}
            getSessionAttendanceInfo={getSessionAttendanceInfo}
          >
          </TypeALayout>
          {isSocketReady && (
            <SocketWrapper debug={debugSocket}>
              <Socket
                siteInfo={siteInfo}
                consoleCfg={consoleCfg}
                debug={debugSocket}
                sessionIdx={sessionIdx}
                eventIdx={eventIdx}
                onConnected={onConnected}
                onDisconnected={onDisconnected}
              />
            </SocketWrapper>
          )}
        </Fragment>
      );
    }
    
};
export default ConsoleViews;
const SocketWrapper = styled.div`
  position: fixed;
  right: 0;
  bottom: 0;
  ${(props) => props.debug && `width:450px; height:250px;`}
`;
